import type { PostHog } from "posthog-js";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import {
  canonicalEvents,
  createCanonicalEventSchemas,
  type CanonicalEventName
} from "../../shared/analytics/canonical-events";
import { isOptedOut, loadOptOutState, getOptOutState } from "./analytics/optOut";
import { logDevEvent } from "./analytics/devLog";

interface AnalyticsConfig {
  posthogKey?: string;
  posthogHost?: string;
  enabled: boolean;
  redactPII: boolean;
  sampleRate: number;
}

const {
  canonicalEventEnum,
  canonicalEventSchemas,
  analyticsEventSchema
} = createCanonicalEventSchemas(z);

type CanonicalAnalyticsEvent = z.infer<typeof analyticsEventSchema>;
const canonicalEventNameSet = new Set<CanonicalEventName>(canonicalEvents.map(event => event.name));
const canonicalEventAllowedKeys = new Map<string, Set<string>>();
const canonicalEventAllowAdditional = new Set<string>();
const globalAllowedKeys = new Set<string>();

for (const event of canonicalEvents) {
  const keys = Object.keys(event.properties);
  canonicalEventAllowedKeys.set(event.name, new Set(keys));
  keys.forEach(key => globalAllowedKeys.add(key));
  if ('allowAdditionalProperties' in event && event.allowAdditionalProperties) {
    canonicalEventAllowAdditional.add(event.name);
  }
}

interface QueuedAnalyticsEvent {
  eventName: string;
  properties: Record<string, unknown>;
  userContext?: {
    userId?: string;
    sessionId?: string;
    organizationId?: string;
  };
  attempts?: number;
  lastError?: string;
}

const ANALYTICS_QUEUE_STORAGE_KEY = 'analytics-event-queue';

/**
 * CANONICAL EVENT TAXONOMY
 * All analytics events should follow these typed interfaces
 */

// Form submission events
export interface FormSubmissionEvent extends Record<string, unknown> {
  submissionId: string;
  templateId?: string;
  formType: 'template' | 'custom';
  userType: 'authenticated' | 'guest';
  conversionStep: 'engagement' | 'conversion';
}

// Billing events
export interface BillingEvent extends Record<string, unknown> {
  organizationId: string;
  priceId?: string;
  subscriptionId?: string;
  planName?: string;
  conversionStep: 'checkout_started' | 'purchase_completed' | 'churn';
}

// Document events
export interface DocumentEvent extends Record<string, unknown> {
  documentId: string;
  documentType?: string;
  organizationId?: string;
  conversionStep: 'engagement';
}

// Invitation events
export interface InvitationEvent extends Record<string, unknown> {
  inviteId: string;
  organizationId: string;
  conversionStep: 'invitation' | 'awareness' | 'activation';
}

class Analytics {
  private config: AnalyticsConfig = {
    enabled: false,
    redactPII: true,
    sampleRate: 1.0
  };

  private posthog: PostHog | null = null;
  private consentGiven: boolean = false;
  private initialized: boolean = false;
  private pendingEvents: QueuedAnalyticsEvent[] = [];
  private isFlushingQueue = false;
  private readonly backendEnabled: boolean = import.meta.env.VITE_ANALYTICS_BACKEND_ENABLED === 'true';
  private readonly maxQueueSize = 50;
  private readonly maxQueueSizeOverride = Number(import.meta.env.VITE_ANALYTICS_MAX_QUEUE_SIZE || 0);

  constructor() {
    // Don't auto-initialize - wait for consent
    this.checkConsentAndInitialize();
    this.initializeQueue();
    // Load opt-out state
    loadOptOutState().catch(err => console.warn('[Analytics] Failed to load opt-out state:', err));
  }

  private initializeQueue() {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.localStorage?.getItem(ANALYTICS_QUEUE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.pendingEvents = parsed.filter((item): item is QueuedAnalyticsEvent => {
            return item && typeof item.eventName === 'string' && item.properties && typeof item.properties === 'object';
          });
        }
      }
    } catch (error) {
      console.warn('[Analytics] Failed to hydrate event queue:', error);
      this.pendingEvents = [];
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this.pendingEvents.length) {
          this.flushQueue().catch(err => console.warn('[Analytics] Failed to flush queued events:', err));
        }
      });
    }

    if (this.backendEnabled && this.pendingEvents.length) {
      this.flushQueue().catch(err => console.warn('[Analytics] Initial queue flush failed:', err));
    }
  }

  private persistQueue() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      if (this.pendingEvents.length === 0) {
        window.localStorage.removeItem(ANALYTICS_QUEUE_STORAGE_KEY);
      } else {
        window.localStorage.setItem(ANALYTICS_QUEUE_STORAGE_KEY, JSON.stringify(this.pendingEvents));
      }
    } catch (error) {
      console.warn('[Analytics] Failed to persist event queue:', error);
    }
  }

  private enqueueEvent(event: QueuedAnalyticsEvent) {
    const limit = this.maxQueueSizeOverride > 0 ? this.maxQueueSizeOverride : this.maxQueueSize;
    if (this.pendingEvents.length >= limit) {
      this.pendingEvents.shift();
    }

    this.pendingEvents.push({ ...event, attempts: event.attempts ?? 0 });
    this.persistQueue();
  }

  private shouldUseBackend(): boolean {
    return this.backendEnabled;
  }

  private sanitizeEventProperties(eventName: string, properties: Record<string, unknown>): Record<string, unknown> {
    const validated = this.validateCanonicalEvent(eventName, properties);
    const allowlisted = this.allowlistEventProperties(eventName, validated);
    return this.redactPII(allowlisted);
  }

  private allowlistEventProperties(eventName: string, properties: Record<string, unknown>): Record<string, unknown> {
    const eventKeys = canonicalEventAllowedKeys.get(eventName);

    if (eventKeys && !canonicalEventAllowAdditional.has(eventName)) {
      return Object.fromEntries(
        Object.entries(properties).filter(([key]) => eventKeys.has(key))
      );
    }

    return Object.fromEntries(
      Object.entries(properties).filter(([key]) => globalAllowedKeys.has(key))
    );
  }

  private validateCanonicalEvent(eventName: string, properties: Record<string, unknown>): Record<string, unknown> {
    if (!canonicalEventNameSet.has(eventName as CanonicalEventName)) {
      return properties;
    }

    const schema = canonicalEventSchemas[eventName as CanonicalEventName];
    const parsed = schema.safeParse(properties);

    if (!parsed.success) {
      console.warn('[Analytics] Canonical event validation failed:', {
        eventName,
        issues: parsed.error.flatten()
      });
      return properties;
    }

    return parsed.data as Record<string, unknown>;
  }

  private async buildUserContext(properties: Record<string, unknown>) {
    let userId: string | undefined;
    let organizationId: string | undefined;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        userId = await this.generateSecurePseudoId(user.id);
      }
    } catch (error) {
      console.warn('Failed to get user for analytics:', error);
    }

    if (typeof properties.organizationId === 'string') {
      organizationId = properties.organizationId;
    }

    return {
      userId,
      sessionId: this.getOrCreateSessionId(),
      organizationId
    };
  }

  private async dispatchToBackend(event: QueuedAnalyticsEvent) {
    if (!this.shouldUseBackend()) {
      return;
    }

    if (typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false) {
      this.enqueueEvent(event);
      return;
    }

    try {
      await this.sendToBackendWithRetry({ ...event });
      if (this.pendingEvents.length) {
        await this.flushQueue();
      }
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: unknown }).message)
        : String(error);

      // Redact event properties before logging errors (prevent PII leakage)
      const sanitizedEventName = event.eventName;

      console.error('[Analytics] Tracking failed:', {
        eventName: sanitizedEventName,
        error: message,
        code: error
        // NEVER log: properties, userContext, or full event object
      });

      this.enqueueEvent({ ...event, lastError: message });
    }
  }

  private async sendToBackendWithRetry(event: QueuedAnalyticsEvent, maxAttempts = 3): Promise<void> {
    let attempt = event.attempts ?? 0;

    while (attempt < maxAttempts) {
      try {
        // Get current session for explicit auth header
        const { data: { session } } = await supabase.auth.getSession();

        // If we don't have a user token yet, skip sending (prevents noisy 401s during hydration)
        if (!session?.access_token) {
          return;
        }
        
        const response = await supabase.functions.invoke('sanitized-analytics', {
          body: {
            eventName: event.eventName,
            properties: event.properties,
            userContext: event.userContext
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (response.error) {
          throw response.error;
        }

        return;
      } catch (error) {
        attempt += 1;
        event.attempts = attempt;

        if (attempt >= maxAttempts) {
          throw error;
        }

        const delay = Math.pow(2, attempt - 1) * 500;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  private async flushQueue() {
    if (this.isFlushingQueue || !this.shouldUseBackend()) {
      return;
    }

    this.isFlushingQueue = true;

    try {
      while (this.pendingEvents.length) {
        const nextEvent = this.pendingEvents[0];

        try {
          await this.sendToBackendWithRetry(nextEvent);
          this.pendingEvents.shift();
          this.persistQueue();
        } catch (error) {
          nextEvent.lastError = error instanceof Error ? error.message : String(error);
          this.persistQueue();
          break;
        }
      }
    } finally {
      this.isFlushingQueue = false;
    }
  }

  private async digest(value: string): Promise<string> {
    // SECURITY: Cryptographically sign consent values using SubtleCrypto
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private async checkConsentAndInitialize() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    // ✅ STEP 1: Check if user is authenticated
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // ✅ STEP 2: Check database FIRST for authenticated users
        const { data: dbConsent, error } = await supabase
          .from('user_consents')
          .select('consent_given, withdrawal_date')
          .eq('user_id', session.user.id)
          .eq('consent_type', 'analytics')
          .maybeSingle();
        
        if (!error && dbConsent && dbConsent.consent_given && !dbConsent.withdrawal_date) {
          // Database says consent is active
          this.consentGiven = true;
          
          // Sync to localStorage for fast subsequent checks
          const value = 'accepted';
          const signature = await this.digest(value);
          window.localStorage.setItem('analytics-consent', `${value}.${signature}`);
          
          await this.initialize();
          
          if (import.meta.env.DEV) {
            console.debug('[Analytics] Consent loaded from database');
          }
          return;
        }
      }
    } catch (error) {
      console.warn('[Analytics] Failed to check database consent:', error);
      // Fall through to localStorage check
    }

    // ✅ STEP 3: Fallback to localStorage (for backward compatibility or unauthenticated users)
    let storedConsent: string | null = null;

    try {
      const raw = window.localStorage.getItem('analytics-consent');
      if (raw) {
        const parts = raw.split('.');
        if (parts.length === 2) {
          const [value, signature] = parts;
          const expectedSignature = await this.digest(value);
          if (signature === expectedSignature) {
            storedConsent = value;
          } else {
            console.warn('[Analytics] Consent signature mismatch - ignoring stored value');
          }
        } else {
          // Legacy format (no signature)
          storedConsent = raw;
        }
      }
    } catch (error) {
      console.warn('Failed to read analytics consent from storage:', error);
    }

    if (storedConsent === 'accepted') {
      this.consentGiven = true;
      this.initialize();
    }
  }

  async giveConsent() {
    this.consentGiven = true;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        // SECURITY: Sign the consent value before storing
        const value = 'accepted';
        const signature = await this.digest(value);
        window.localStorage.setItem('analytics-consent', `${value}.${signature}`);
      } catch (error) {
        console.warn('Failed to persist analytics consent:', error);
      }
    }
    if (!this.initialized) {
      await this.initializeWithRetry();
    }
  }

  private async initializeWithRetry(maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.initialize();
        return;
      } catch (error) {
        console.error(`[Analytics] Initialization attempt ${attempt}/${maxRetries} failed:`, error);
        
        if (attempt < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          throw new Error('Analytics initialization failed after max retries');
        }
      }
    }
  }

  async revokeConsent() {
    this.consentGiven = false;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        // SECURITY: Sign the rejected value as well
        const value = 'rejected';
        const signature = await this.digest(value);
        window.localStorage.setItem('analytics-consent', `${value}.${signature}`);
      } catch (error) {
        console.warn('Failed to persist analytics consent:', error);
      }
    }
    if (this.posthog) {
      this.posthog.opt_out_capturing();
    }
  }

  private async initialize() {
    if (this.initialized || !this.consentGiven) return;

    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
    
    if (posthogKey) {
      this.config = {
        posthogKey,
        posthogHost,
        enabled: true,
        redactPII: true,
        sampleRate: 1.0
      };

      await this.loadPostHog();
      this.initialized = true;
    }
  }

  private async loadPostHog() {
    if (!this.consentGiven) return;
    
    try {
      const { default: posthog } = await import('posthog-js');
      posthog.init(this.config.posthogKey!, {
        api_host: this.config.posthogHost,
        person_profiles: 'never', // No personal profiles for privacy
        capture_pageview: false, // Manual pageview tracking
        disable_session_recording: false,
        session_recording: {
          // OWASP A03 (Injection) - Mask all user inputs
          maskAllInputs: true,
          
          // HIPAA/SOC 2 - Aggressive text masking for extra safety
          maskTextSelector: '*',
          
          // OWASP A05 (Security Misconfiguration) - No cross-origin recording
          recordCrossOriginIframes: false
        },
        opt_out_capturing_by_default: false,
        respect_dnt: true,
        persistence: 'localStorage'
      });
      this.posthog = posthog;
      console.log('PostHog initialized with consent');
    } catch (error) {
      console.warn('PostHog failed to load:', error);
    }
  }

  /**
   * SECURITY: Dynamically control session recording based on current route
   * Only allows recording on safe, non-PII pages
   * 
   * Compliance: SOC 2 (CC6.7), HIPAA §164.514(b), GDPR Art 25
   */
  public updateSessionRecordingForRoute(pathname: string): void {
    if (!this.posthog || !this.consentGiven) return;

    // Define safe routes (must match urlTriggers above)
    const safeRoutes = [
      '/',
      '/landing',
      '/pricing',
      '/privacy',
      '/terms',
      '/faq',
      '/login',
      '/signup',
      '/org/signup'
    ];

    const isSafeRoute = safeRoutes.includes(pathname);

    try {
      if (isSafeRoute) {
        // Start recording on safe marketing pages
        this.posthog.startSessionRecording();
        
        if (import.meta.env.DEV) {
          console.debug('[Analytics] Session recording STARTED for:', pathname);
        }
      } else {
        // Stop recording on any sensitive page (forms, dashboards, vault)
        this.posthog.stopSessionRecording();
        
        if (import.meta.env.DEV) {
          console.debug('[Analytics] Session recording STOPPED for:', pathname);
        }
      }
    } catch (error) {
      console.error('[Analytics] Failed to update session recording:', error);
      // Fail-secure: If error, ensure recording is stopped
      try {
        this.posthog.stopSessionRecording();
      } catch (stopError) {
        console.error('[Analytics] Failed to stop recording on error:', stopError);
      }
    }
  }

  private redactPII(props: Record<string, unknown>): Record<string, unknown> {
    if (!this.config.redactPII) return props;

    const piiKeys = [
      'email',
      'phone',
      'address',
      'message',
      'name',
      'first_name',
      'last_name',
      'ssn',
      'social_security',
      'tax_id'
    ];
    const redacted: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(props)) {
      const keyLower = key.toLowerCase();
      
      // Check if key contains PII indicators
      const containsPII = piiKeys.some(piiKey => keyLower.includes(piiKey));
      
      if (containsPII) {
        // Skip PII fields entirely
        continue;
      }

      // For text fields longer than 100 chars, might be free text
      if (typeof value === 'string' && value.length > 100) {
        continue;
      }

      redacted[key] = value;
    }

    return redacted;
  }

  // SECURITY: Server-side only pseudonymization - no client secrets
  private async generateSecurePseudoId(userId: string): Promise<string> {
    try {
      const response = await supabase.functions.invoke('generate-pseudo-id', {
        body: { userId },
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (response.error) {
        throw new Error(`Server error: ${response.error.message}`);
      }
      
      if (response.data?.pseudoId) {
        return response.data.pseudoId;
      }
      
      throw new Error('Server-side pseudo ID generation failed');
    } catch (error) {
      console.error('Failed to generate secure pseudo ID:', error);
      // SECURITY: No client-side fallback - fail securely
      throw new Error('Analytics unavailable: secure pseudonymization required');
    }
  }

  private getOrCreateSessionId(): string {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return Math.random().toString(36).slice(2);
    }

    try {
      const existing = window.sessionStorage.getItem('analytics_session_id');
      if (existing) return existing;

      const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      window.sessionStorage.setItem('analytics_session_id', sessionId);
      return sessionId;
    } catch (error) {
      console.warn('Failed to access sessionStorage for analytics session id:', error);
      return Math.random().toString(36).slice(2);
    }
  }

  async track(eventName: string, properties: Record<string, unknown> = {}) {
    // Check opt-out status first
    if (isOptedOut()) {
      const optOutState = getOptOutState();
      const reason = optOutState.userOptedOut 
        ? "User opted out" 
        : `Organization ${optOutState.orgId} opted out`;
      
      logDevEvent(eventName, properties, true, reason);
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] Event blocked (opt-out):', eventName, reason);
      }
      return;
    }

    // Respect consent and configuration
    if (!this.consentGiven || !this.config.enabled || Math.random() > this.config.sampleRate) {
      const reason = !this.consentGiven 
        ? "No consent" 
        : !this.config.enabled 
          ? "Analytics disabled" 
          : "Sample rate excluded";
      
      logDevEvent(eventName, properties, true, reason);
      
      if (import.meta.env.DEV) {
        console.warn('[Analytics] Event blocked:', {
          eventName,
          consentGiven: this.consentGiven,
          enabled: this.config.enabled,
          sampleRate: this.config.sampleRate
        });
      }
      return;
    }

    // Log to dev log (not blocked)
    logDevEvent(eventName, properties, false);

    try {
      const cleanProps = this.sanitizeEventProperties(eventName, properties);
      const userContext = await this.buildUserContext(cleanProps);

      // Track to PostHog if available
      if (this.posthog && this.consentGiven) {
        this.posthog.capture(eventName, {
          ...cleanProps,
          ...userContext,
          timestamp: Date.now()
        });
      }

      await this.dispatchToBackend({
        eventName,
        properties: cleanProps,
        userContext
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }

  // Enhanced tracking methods for billing-relevant events
  async trackUsage(eventType: 'pdf_filled' | 'pdf_export' | 'form_submitted' | 'invite_sent', metadata: Record<string, unknown> = {}) {
    // Redact PII from metadata before sending to any external service
    const cleanMetadata = this.redactPII(metadata);

    // Record usage for billing purposes
    await supabase.functions.invoke('record-usage', {
      body: {
        event_type: eventType,
        quantity: 1,
        metadata: cleanMetadata
      }
    });

    // Also track for analytics (already cleaned)
    await this.track(`usage_${eventType}`, { event_type: eventType, ...cleanMetadata });
  }

  // Convenience methods for common events
  async trackInviteSent(inviteId: string, orgId: string, metadata: Record<string, unknown> = {}) {
    const payload = {
      inviteId,
      organizationId: orgId,
      ...metadata
    };

    await this.track('invite_sent', {
      inviteId,
      organizationId: orgId,
      conversionStep: 'invitation',
      ...metadata
    });

    await this.trackUsage('invite_sent', payload);
  }

  async trackInviteOpened(inviteId: string, userAgent?: string) {
    await this.track('invite_opened', {
      inviteId,
      userAgent,
      conversionStep: 'awareness'
    });
  }

  async trackOrgSignup(orgId: string, plan?: string) {
    await this.track('org_signup', { orgId, plan });
  }

  async trackFormOpened(submissionId: string, templateId?: string) {
    await this.track('form_opened', { submissionId, templateId });
  }

  async trackSubmissionStarted(submissionId: string, templateId?: string, userType?: 'authenticated' | 'guest') {
    await this.track('submission_started', {
      submissionId,
      templateId,
      formType: templateId ? 'template' : 'custom',
      userType,
      conversionStep: 'engagement'
    });
  }

  async trackSubmissionCompleted(submissionId: string, channel?: string, templateId?: string) {
    await this.track('submission_completed', {
      submissionId,
      channel: channel || 'web',
      completionTime: Date.now(),
      conversionStep: 'conversion'
    });
    
    // Also record for billing
    await this.trackUsage('form_submitted', { submissionId, templateId });
  }

  async trackPdfExported(documentId: string, templateId?: string, exportType?: 'download' | 'share' | 'email') {
    await this.track('pdf_exported', {
      documentId,
      templateId,
      exportType: exportType || 'download',
      timestamp: Date.now()
    });
    
    // Also record for billing
    await this.trackUsage('pdf_export', { documentId, templateId, exportType });
  }

  async trackPdfFilled(documentId: string, templateId?: string, fieldCount?: number) {
    await this.track('pdf_filled', {
      documentId,
      templateId,
      fieldCount,
      timestamp: Date.now()
    });
    
    // Also record for billing
    await this.trackUsage('pdf_filled', { documentId, templateId, fieldCount });
  }

  async trackDocumentUploaded(documentType?: string, fileSize?: number, uploadMethod?: string) {
    await this.track('document_uploaded', {
      documentType,
      fileSize,
      uploadMethod
    });
  }

  async trackDocumentUpdated(documentType?: string, fileSize?: number, updateMethod?: string) {
    await this.track('document_updated', {
      documentType,
      fileSize,
      updateMethod,
      action: 'replace'
    });
  }

  async trackExpiryNoticeSent(reminderType: string, documentType?: string, daysUntilExpiry?: number, channel?: 'email' | 'push', urgency?: 'low' | 'medium' | 'high') {
    await this.track('expiry_notice_sent', {
      reminderType,
      documentType,
      daysUntilExpiry,
      channel,
      urgency
    });
  }

  // Billing events (typed)
  async trackBillingCheckoutStarted(organizationId: string, priceId: string, planName?: string) {
    const event: BillingEvent = {
      organizationId,
      priceId,
      planName,
      conversionStep: 'checkout_started'
    };
    await this.track('billing_checkout_started', event);
  }

  async trackBillingCheckoutCompleted(organizationId: string, subscriptionId: string, planName?: string) {
    const event: BillingEvent = {
      organizationId,
      subscriptionId,
      planName,
      conversionStep: 'purchase_completed'
    };
    await this.track('billing_checkout_completed', event);
  }

  async trackBillingSubscriptionCancelled(organizationId: string, subscriptionId: string, reason?: string) {
    const event: BillingEvent & { reason?: string } = {
      organizationId,
      subscriptionId,
      conversionStep: 'churn',
      reason
    };
    await this.track('billing_subscription_cancelled', event);
  }

  // Vault events
  async trackVaultDocumentDownloaded(documentId: string, documentType?: string, organizationId?: string) {
    await this.track('vault_document_downloaded', {
      documentId,
      documentType,
      organizationId,
      timestamp: Date.now()
    });
  }

  // PDF intake events
  async trackPdfIntakeFailed(errorCode: string, errorMessage?: string, organizationId?: string) {
    await this.track('pdf_intake_failed', {
      errorCode,
      errorMessage,
      organizationId,
      timestamp: Date.now()
    });
  }

  // Org invite events
  async trackOrgInviteSent(invitationId: string, organizationId: string, recipientEmail: string) {
    const sanitizedRecipient = this.redactPII({ recipientEmail });

    await this.track('org_invite_sent', {
      invitationId,
      organizationId,
      ...(sanitizedRecipient.recipientEmail ? { recipientEmail: sanitizedRecipient.recipientEmail } : {}),
      timestamp: Date.now()
    });
  }

  async trackOrgInviteAccepted(invitationId: string, organizationId: string) {
    await this.track('org_invite_accepted', {
      invitationId,
      organizationId,
      timestamp: Date.now()
    });
  }

  getConfig(): AnalyticsConfig {
    return { ...this.config };
  }

  updateConfig(updates: Partial<AnalyticsConfig>) {
    this.config = { ...this.config, ...updates };
  }

  hasConsent(): boolean {
    return this.consentGiven;
  }
}

export const analytics = new Analytics();
export type { AnalyticsConfig, CanonicalAnalyticsEvent, CanonicalEventName };
export { canonicalEventEnum, canonicalEventSchemas, analyticsEventSchema };
